import { Button, DialogFooter, Input, Label } from "@istari-frontend/ui";
import { NotebookPen } from "lucide-react";
import { type FormEvent, type MutableRefObject, useId, useState } from "react";
import { toast } from "sonner";

import { type ConfigurationTrackedFile } from "~/api/configurations";
import { useCreateNotebook } from "~/notebook/feature/use-create-notebook";

export type CreateNotebookResult = {
  resourceId: string;
  fileRevisionId: string;
  configId: string;
  snapshotId: string;
};

export type NotebookCreateSection = {
  systemId: string;
  branchId: string;
  expectedHeadSnapshotId: string;
  existingTrackedFiles: ConfigurationTrackedFile[];
  sourceConfigId?: string;
  defaultName?: string;
  onCreated: (created: CreateNotebookResult) => void;
};

type NotebookCreateTabProps = {
  // Passed as a ref so handleSubmit reads the freshest payload at click time, not the snapshot
  // pinned by the render closure when the modal first opened.
  sectionRef: MutableRefObject<NotebookCreateSection | undefined>;
  onClose: () => void;
};

export function NotebookCreateTab({ sectionRef, onClose }: NotebookCreateTabProps) {
  const nameInputId = useId();
  const [name, setName] = useState(sectionRef.current?.defaultName ?? "");
  const createNotebookMutation = useCreateNotebook();

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !createNotebookMutation.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const section = sectionRef.current;
    if (!section) return;
    createNotebookMutation.mutate(
      {
        displayName: trimmed,
        systemId: section.systemId,
        branchId: section.branchId,
        expectedHeadSnapshotId: section.expectedHeadSnapshotId,
        configName: `Add ${trimmed}`,
        existingTrackedFiles: section.existingTrackedFiles,
        sourceConfigId: section.sourceConfigId,
      },
      {
        onSuccess: (result) => {
          setName("");
          onClose();
          sectionRef.current?.onCreated({
            resourceId: result.resourceId,
            fileRevisionId: result.fileRevisionId,
            configId: result.configId,
            snapshotId: result.snapshotId,
          });
        },
        onError: () => {
          toast.error("Couldn't create notebook.");
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col items-center justify-center gap-4 py-8">
        <NotebookPen className="text-muted-foreground h-12 w-12" />
        <p className="text-muted-foreground max-w-xs text-center text-sm">
          Create a new notebook tracked in this system. You can reference any of the system&apos;s files from inside its
          cells.
        </p>
        <div className="flex w-full max-w-xs flex-col gap-2">
          <Label htmlFor={nameInputId}>Name</Label>
          <Input
            id={nameInputId}
            autoFocus
            placeholder="Name your notebook…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createNotebookMutation.isPending}
          />
          <div className="text-muted-foreground text-xs">
            Saved as{" "}
            <span className="font-mono">{(trimmed.length > 0 ? trimmed : "name").replace(/\.ipynb$/i, "")}.ipynb</span>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={createNotebookMutation.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit} data-testid="create-notebook-submit-btn">
          {createNotebookMutation.isPending ? "Creating…" : "Create notebook"}
        </Button>
      </DialogFooter>
    </form>
  );
}
